import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Stack,
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Alert,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  SupportAgent as HelpdeskIcon,
  Description as FormIcon,
  ArrowForward as OpenPageIcon,
  AutoAwesome as GuidedIcon,
  Send as SubmitIcon,
  ThumbUpOffAlt as ThumbUpOutlineIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDownOffAlt as ThumbDownOutlineIcon,
  ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import { useAssistant, type AssistantMessage, type CollectCard } from '../AssistantContext';
import PendingAttachments from './PendingAttachments';
import type { AssistantSource } from '../api/assistant';

/**
 * Deflection-catalog targets are admin-editable at runtime rather than source-controlled, so a
 * `targetValue` is only trusted as an href once its scheme is confirmed http(s). Rejects
 * `javascript:` and protocol-relative `//evil.example`; returns null when unusable, in which case
 * the link is not rendered.
 */
const safeExternalHref = (href?: string): string | null => {
  const value = href?.trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
};

/**
 * An `askiam-page` target is an in-app React route rendered via the router, never an href. Only a
 * root-relative path is trusted: it must start with a single `/` (rejecting protocol-relative
 * `//evil.example`, which the router would treat as an external origin — an open-redirect guard).
 * Returns null when unusable, in which case no action is rendered.
 */
const safeRelativePath = (path?: string): string | null => {
  const value = path?.trim();
  if (!value) return null;
  return value.startsWith('/') && !value.startsWith('//') ? value : null;
};

/** One server-authoritative path, rendered as a card with a single actionable affordance. */
export const SourceCard: React.FC<{
  source: AssistantSource;
  onNavigate: () => void;
  /** When provided, `askiam-request-type` cards offer a guided in-chat collection alongside the form. */
  onStartCollect?: (requestType: string, displayName: string) => void;
}> = ({ source, onNavigate, onStartCollect }) => {
  const navigate = useNavigate();
  const externalHref = safeExternalHref(source.targetValue);

  const action = (() => {
    switch (source.targetType) {
      case 'askiam-request-type':
        return (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {onStartCollect && source.targetValue && (
              <Button
                size="small"
                variant="contained"
                startIcon={<GuidedIcon fontSize="small" />}
                onClick={() => onStartCollect(source.targetValue!, source.title)}
              >
                Start guided request
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<FormIcon fontSize="small" />}
              onClick={() => {
                onNavigate();
                navigate(`/requests/new/${source.targetValue}`);
              }}
            >
              Open form
            </Button>
          </Stack>
        );
      case 'askiam-page': {
        const relPath = safeRelativePath(source.targetValue);
        return relPath ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenPageIcon fontSize="small" />}
            onClick={() => {
              onNavigate();
              navigate(relPath);
            }}
          >
            Open
          </Button>
        ) : null;
      }
      case 'url':
      case 'icm':
        return externalHref ? (
          <Button
            size="small"
            variant="outlined"
            component="a"
            href={externalHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon fontSize="small" />}
          >
            {source.targetType === 'icm' ? 'Open ICM' : 'Open link'}
          </Button>
        ) : null;
      case 'ghd':
        return externalHref ? (
          <Button
            size="small"
            variant="outlined"
            component="a"
            href={externalHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<HelpdeskIcon fontSize="small" />}
          >
            Global Helpdesk
          </Button>
        ) : null;
      default:
        return null;
    }
  })();

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2">{source.title}</Typography>
          {source.matchStrength > 0 && (
            <Chip label={`${source.matchStrength}% match`} size="small" variant="outlined" />
          )}
        </Stack>
        {source.description && (
          <Typography variant="body2" color="text.secondary">
            {source.description}
          </Typography>
        )}
        {source.targetType === 'instructions' && source.targetValue && (
          <Typography
            variant="body2"
            component="pre"
            sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0, color: 'text.secondary' }}
          >
            {source.targetValue}
          </Typography>
        )}
        {action && <Box>{action}</Box>}
      </Stack>
    </Paper>
  );
};

/**
 * Thumbs up/down on one assistant answer. A 👍 reinforces the surfaced path for future questions; a
 * 👎 is a negative signal. Best-effort — the vote is fire-and-forget server-side, so the control only
 * reflects the local selection and never blocks or errors the conversation. Votes can be flipped.
 */
const AnswerFeedback: React.FC<{ value?: 'up' | 'down'; onVote: (helpful: boolean) => void }> = ({ value, onVote }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pl: 0.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
      Was this helpful?
    </Typography>
    <Tooltip title="This helped">
      <IconButton
        size="small"
        color={value === 'up' ? 'primary' : 'default'}
        aria-label="Mark this answer helpful"
        aria-pressed={value === 'up'}
        onClick={() => onVote(true)}
      >
        {value === 'up' ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlineIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
    <Tooltip title="This didn't help">
      <IconButton
        size="small"
        color={value === 'down' ? 'error' : 'default'}
        aria-label="Mark this answer not helpful"
        aria-pressed={value === 'down'}
        onClick={() => onVote(false)}
      >
        {value === 'down' ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlineIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  </Stack>
);

/**
 * The in-chat outcome of a guided collection: either an editable review of the assembled draft with
 * a Confirm & Submit, or a hand-off to the prefilled form for templates that can't be fully collected
 * in chat (tenant pickers, dynamic lookups, attachments, etc.). The draft is advisory — the server
 * re-validates the whole payload on submit.
 */
const CollectionCard: React.FC<{
  card: CollectCard;
  /** True only for the live card (the last turn while a collection is open). Stale/superseded cards are inert. */
  active?: boolean;
  busy?: boolean;
  onNavigate: () => void;
  onConfirmCollect?: () => void;
  onRefreshCollect?: (values: Record<string, string>) => void;
  onCancelCollect?: () => void;
}> = ({ card, active, busy, onNavigate, onConfirmCollect, onRefreshCollect, onCancelCollect }) => {
  const navigate = useNavigate();
  const { evidenceFiles, setEvidenceFiles, reviewValues, setReviewValues, savedRequestId } = useAssistant();
  const nativeEvidence = card.attachments?.enabled && card.attachments.provider === 'ado';
  const formPath = safeRelativePath(card.formUrl) ?? `/requests/new/${card.requestType}`;
  const externalHref = safeExternalHref(card.externalUrl);
  const effectiveReviewValue = (field: NonNullable<CollectCard['reviewFields']>[number]) =>
    reviewValues[field.key] ?? card.collected[field.key] ?? '';
  const missingRequiredReview = card.reviewFields?.some(
    field => field.required && (
      field.type === 'checkbox'
        ? effectiveReviewValue(field) !== 'true'
        : !effectiveReviewValue(field).trim()
    ),
  ) ?? false;

  const openInForm = () => {
    const prefill = { ...card.collected };
    for (const field of card.reviewFields ?? []) {
      if (!(field.key in reviewValues)) continue;
      const value = reviewValues[field.key].trim();
      if (value) prefill[field.key] = value;
      else delete prefill[field.key];
    }
    // End the active collection before leaving. AssistantProvider is shared across surfaces, so
    // without this the status stays 'handoff' and reopening the assistant would show a disabled
    // composer and this stale card as active until the user manually cancels.
    if (active) onCancelCollect?.();
    onNavigate();
    navigate(formPath, { state: { prefill } });
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, width: '100%', opacity: active ? 1 : 0.7 }}>
      <Stack spacing={1.25}>
        <Typography variant="subtitle2">
          {card.status === 'review'
            ? `Review your ${card.displayName} request`
            : externalHref
              ? `Continue outside AskIAM`
              : `Continue in the ${card.displayName} form`}
        </Typography>

        {card.status === 'review' && card.titlePreview && (
          <Typography variant="body2" color="text.secondary">
            <strong>Title:</strong> {card.titlePreview}
          </Typography>
        )}

        {card.status === 'review' && card.draft && card.draft.length > 0 && (
          <Stack spacing={0.5} sx={{ pl: 0.5 }}>
            {card.draft.map((item) => (
              <Box key={item.key}>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {item.value || '—'}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {active ? (
          <>
          {card.status === 'review' && <>
            {savedRequestId && <Alert severity="info">
              The bug is saved. Confirm again to retry only its remaining attachments.{' '}
              <a href={`/requests/${savedRequestId}`}>View saved bug</a>
            </Alert>}
            {card.reviewFields?.map((field) => field.type === 'checkbox' ? (
              <Box key={field.key}>
                <FormControlLabel
                  control={<Checkbox
                    checked={effectiveReviewValue(field) === 'true'}
                    disabled={busy || !!savedRequestId}
                    onChange={event => {
                      const next = {
                        ...reviewValues,
                        [field.key]: event.target.checked ? 'true' : field.required ? 'false' : '',
                      };
                      setReviewValues(next);
                      onRefreshCollect?.(next);
                    }}
                  />}
                  label={`${field.label}${field.required ? ' *' : ' (optional)'}`}
                />
                {field.required && effectiveReviewValue(field) !== 'true' && (
                  <FormHelperText error>This acknowledgement is required.</FormHelperText>
                )}
              </Box>
            ) : (
              <TextField
                key={field.key}
                select={field.type === 'select'}
                type={field.type === 'number' || field.type === 'email' || field.type === 'url' || field.type === 'date'
                  ? field.type
                  : undefined}
                multiline={field.type === 'textarea' || field.type === 'repeatableText'}
                minRows={field.type === 'textarea' || field.type === 'repeatableText' ? 2 : undefined}
                label={`${field.label}${field.required ? ' *' : ' (optional)'}`}
                value={effectiveReviewValue(field)}
                disabled={busy || !!savedRequestId}
                helperText={field.type === 'repeatableText'
                  ? field.helperText ?? 'Enter one value per line.'
                  : field.helperText}
                onChange={event => {
                  const value = String(event.target.value ?? '');
                  const next = { ...reviewValues, [field.key]: value };
                  setReviewValues(next);
                  if (field.type === 'select' && value !== (card.collected[field.key] ?? '')) {
                    onRefreshCollect?.(next);
                  }
                }}
                onBlur={event => {
                  const next = { ...reviewValues, [field.key]: String(event.target.value ?? '') };
                  if (next[field.key] !== (card.collected[field.key] ?? '')) {
                    onRefreshCollect?.(next);
                  }
                }}
              >
                {field.type === 'select'
                  ? [
                    ...(!field.required
                      ? [<MenuItem key="__blank" value=""><em>Leave blank</em></MenuItem>]
                      : []),
                    ...(field.options?.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    )) ?? []),
                  ]
                  : undefined}
              </TextField>
            ))}
            {nativeEvidence && (
              <PendingAttachments
                files={evidenceFiles}
                onFilesChange={setEvidenceFiles}
                disabled={busy || !!savedRequestId}
              />
            )}
          </>}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {card.status === 'review' ? (
              <>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy || missingRequiredReview}
                  startIcon={<SubmitIcon fontSize="small" />}
                  onClick={() => onConfirmCollect?.()}
                >
                  {savedRequestId ? 'Retry remaining attachments' : 'Confirm & Submit'}
                </Button>
                <Button size="small" variant="outlined" disabled={busy || !!savedRequestId || evidenceFiles.length > 0} startIcon={<FormIcon fontSize="small" />} onClick={openInForm}>
                  Open in form
                </Button>
              </>
            ) : externalHref ? (
              <Button
                size="small"
                variant="contained"
                component="a"
                href={externalHref}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon fontSize="small" />}
                onClick={() => {
                  if (active) onCancelCollect?.();
                }}
              >
                Open link
              </Button>
            ) : (
              <Button size="small" variant="contained" startIcon={<FormIcon fontSize="small" />} onClick={openInForm}>
                Open prefilled form
              </Button>
            )}
            <Button size="small" color="inherit" disabled={busy} onClick={() => onCancelCollect?.()}>
              Cancel
            </Button>
          </Stack>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            This step is no longer active.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

/** A single chat bubble plus, for assistant guidance turns, its source cards and helpdesk link. */
export const MessageBubble: React.FC<{
  message: AssistantMessage;
  onNavigate: () => void;
  onFeedback?: (helpful: boolean) => void;
  onStartCollect?: (requestType: string, displayName: string) => void;
  onConfirmCollect?: () => void;
  onRefreshCollect?: (values: Record<string, string>) => void;
  onCancelCollect?: () => void;
  busy?: boolean;
  /** True when this turn's collection card is the live one (drives whether its actions render). */
  collectCardActive?: boolean;
}> = ({ message, onNavigate, onFeedback, onStartCollect, onConfirmCollect, onRefreshCollect, onCancelCollect, busy, collectCardActive }) => {
  const isUser = message.role === 'user';
  return (
    <Stack spacing={1} sx={{ alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      <Paper
        variant={isUser ? 'elevation' : 'outlined'}
        elevation={isUser ? 2 : 0}
        sx={{
          p: 1.25,
          maxWidth: '85%',
          bgcolor: isUser ? 'primary.main' : 'background.paper',
          color: isUser ? 'primary.contrastText' : message.error ? 'error.main' : 'text.primary',
          borderColor: message.error ? 'error.main' : undefined,
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {message.content}
        </Typography>
      </Paper>

      {!isUser && message.collect && (
        <CollectionCard
          card={message.collect}
          active={!!collectCardActive}
          busy={busy}
          onNavigate={onNavigate}
          onConfirmCollect={onConfirmCollect}
          onRefreshCollect={onRefreshCollect}
          onCancelCollect={onCancelCollect}
        />
      )}

      {!isUser && message.sources && message.sources.length > 0 && (
        <Stack spacing={1} sx={{ width: '100%' }}>
          {message.sources.map((s, i) => (
            <SourceCard
              key={`${s.targetType}:${s.targetValue}:${i}`}
              source={s}
              onNavigate={onNavigate}
              onStartCollect={onStartCollect}
            />
          ))}
        </Stack>
      )}

      {!isUser && !message.error && message.fallback === 'ghd' && message.helpdeskUrl && (
        <Button
          size="small"
          variant="contained"
          component="a"
          href={message.helpdeskUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<HelpdeskIcon fontSize="small" />}
        >
          Go to Global Helpdesk
        </Button>
      )}

      {!isUser && !message.error && onFeedback && (
        <AnswerFeedback value={message.feedback} onVote={onFeedback} />
      )}
    </Stack>
  );
};

/**
 * The message list for one assistant conversation, shared by the chat modal and the home page so a
 * bubble, a source card and the "Thinking…" indicator render identically wherever the thread is
 * shown. Auto-scrolls its tail into view as turns arrive (unless `autoScroll` is disabled).
 * `onNavigate` runs just before a source card navigates in-app — the modal uses it to close itself;
 * the inline home thread passes a no-op.
 */
export const ConversationThread: React.FC<{
  messages: AssistantMessage[];
  loading: boolean;
  onNavigate: () => void;
  autoScroll?: boolean;
  /** Records a thumbs up/down on the answer at the given index. Omit to hide the feedback control. */
  onFeedback?: (index: number, helpful: boolean) => void;
  /** Begins a guided in-chat collection for a request type. Omit to hide the "Start guided request" button. */
  onStartCollect?: (requestType: string, displayName: string) => void;
  /** Confirms and submits the assembled review draft. */
  onConfirmCollect?: () => void;
  /** Revalidates an edited review draft and replaces it with the canonical server response. */
  onRefreshCollect?: (values: Record<string, string>) => void;
  /** Abandons the active collection. */
  onCancelCollect?: () => void;
  /** True while a guided collection is live (collectStatus !== 'idle'); keeps the newest card actionable. */
  collectionActive?: boolean;
}> = ({ messages, loading, onNavigate, autoScroll = true, onFeedback, onStartCollect, onConfirmCollect, onRefreshCollect, onCancelCollect, collectionActive }) => {
  const listEndRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (autoScroll) listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, autoScroll]);

  // The single live collection card is the newest message that carries one, and only while a
  // collection is active. Keying off this (rather than "is last message") keeps the card actionable
  // when a non-card turn is appended after it — e.g. a failed-submission error notice — so the user
  // can still retry or cancel instead of being stranded.
  let lastCollectIdx = -1;
  for (let i = 0; i < messages.length; i += 1) {
    if (messages[i].collect) lastCollectIdx = i;
  }

  return (
    <Stack spacing={2} sx={{ flexGrow: 1 }}>
      {messages.map((m, i) => (
        <MessageBubble
          key={i}
          message={m}
          onNavigate={onNavigate}
          onFeedback={onFeedback ? (helpful) => onFeedback(i, helpful) : undefined}
          onStartCollect={onStartCollect}
          onConfirmCollect={onConfirmCollect}
          onRefreshCollect={onRefreshCollect}
          onCancelCollect={onCancelCollect}
          busy={loading}
          collectCardActive={!!collectionActive && i === lastCollectIdx}
        />
      ))}
      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Thinking…</Typography>
        </Stack>
      )}
      <div ref={listEndRef} />
    </Stack>
  );
};
